'use client';

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faEdit, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';
import Spinner from '../spinner';
import TiptapEditor from './TiptapEditor';
import EditorToolbar from './EditorToolbar';
import { getUserIdentity } from '../../utils/authorization';
import ProfessionalSidebar from './ProfessionalSidebar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

import { extractHeadings } from './utils/headingUtils';
import Button from '../button-v1';
import styles from './HelpDocumentationPage.module.css';

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '../ui/dialog';

import useRoles from '../../hooks/useRoles';
import { NAVBAR_LINKS_DATA } from '../Navbar';

import {
  getAllHelpSections,
  getHelpSection,
  createHelpSection,
  updateHelpSection,
  deleteHelpSection,
  uploadHelpImage,
} from '../../services/help-documentation/helpDocumentation.service';

const slugify = (text = '') =>
  text.toLowerCase().trim().replace(/[\s\/]+/g, '-').replace(/[^\w-]+/g, '') || '';

/**
 * Normalize TipTap content to fix common issues:
 * 1. Strip marks from H1 text nodes (ensure plain text)
 * 2. Remove empty text nodes from paragraphs (fixes blank editor issue)
 */
const normalizeTipTapContent = (doc) => {
  if (!doc || typeof doc !== 'object' || !Array.isArray(doc.content)) {
    return doc;
  }

  const normalizeNode = (node) => {
    // If it's a text node, strip marks if this is inside an H1
    if (node.type === 'text') {
      return { type: 'text', text: node.text || '' };
    }

    // If it's a heading at level 1, ensure all text children have no marks
    if (node.type === 'heading' && node.attrs?.level === 1) {
      const cleanContent = Array.isArray(node.content)
        ? node.content.map(normalizeNode).filter(child => {
            // Remove empty text nodes
            if (child.type === 'text' && !child.text) return false;
            return true;
          })
        : [];
      return {
        ...node,
        attrs: { ...node.attrs, level: 1 },
        content: cleanContent,
      };
    }

    // For paragraphs, remove empty text nodes from content
    if (node.type === 'paragraph') {
      if (!Array.isArray(node.content) || node.content.length === 0) {
        // Empty paragraph - return with no content
        return { type: 'paragraph', attrs: node.attrs || {} };
      }

      // Filter out empty text nodes
      const cleanContent = node.content.map(normalizeNode).filter(child => {
        if (child.type === 'text' && !child.text) return false;
        return true;
      });

      // If all content was empty text nodes, return empty paragraph
      if (cleanContent.length === 0) {
        return { type: 'paragraph', attrs: node.attrs || {} };
      }

      return {
        ...node,
        content: cleanContent,
      };
    }

    // Recursively normalize other nodes
    if (Array.isArray(node.content)) {
      return {
        ...node,
        content: node.content.map(normalizeNode),
      };
    }

    return node;
  };

  return {
    ...doc,
    content: doc.content.map(normalizeNode),
  };
};

const createEmptySection = (title) => {
  const displayTitle = title?.trim() || 'Untitled Section';
  return {
    type: 'doc',
    content: [
      // H1 with plain text (no marks)
      { type: 'heading', attrs: { level: 1 }, content: [{ type: 'text', text: displayTitle }] },
      // Empty paragraph (no content array - this is the valid TipTap empty paragraph)
      { type: 'paragraph' },
    ],
  };
};

export default function HelpDocumentationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const editorRef = useRef(null);
  const editorScrollRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [activeSection, setActiveSection] = useState(null);
  const [activeSectionContent, setActiveSectionContent] = useState(null);

  const [isEditing, setIsEditing] = useState(false);
  const [sectionDraft, setSectionDraft] = useState(null);

  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [selectedNavLink, setSelectedNavLink] = useState('');
  const [titleEdited, setTitleEdited] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
   const isSuperAdmin = getUserIdentity()?.isSuperAdmin ?? false;
   const isTocauser = getUserIdentity()?.isToca ?? false;
  const { roles } = useRoles();

  const [scrollTarget, setScrollTarget] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [imageDialogSrc, setImageDialogSrc] = useState(null);

  const handleImageClick = useCallback((src) => {
    if (!src) return;
    setImageDialogSrc(src);
    setImageDialogOpen(true);
  }, []);

  const normalizedNavbarLinks = useMemo(() => {
    if (!NAVBAR_LINKS_DATA || typeof NAVBAR_LINKS_DATA !== 'object') return [];
    const flat = Array.isArray(NAVBAR_LINKS_DATA) ? NAVBAR_LINKS_DATA : Object.values(NAVBAR_LINKS_DATA).flat();
    const map = new Map();
    flat.forEach((l) => { if (l?.path) map.set(l.path, l); });
    return Array.from(map.values());
  }, []);

  const fetchAllSections = useCallback(async () => {
    const res = await getAllHelpSections();
    return res?.data || [];
  }, []);

  const {
    data: sections = [],
    isLoading: sectionsIsLoading,
    refetch: refetchSections,
  } = useQuery(['help-sections'], fetchAllSections, {
    staleTime: 1000 * 60 * 2,
  });

  const availableNavLinks = useMemo(() => {
    const used = new Set((sections || []).map((s) => s.nav_link_path).filter(Boolean));
    return normalizedNavbarLinks.filter((l) => l && !used.has(l.path));
  }, [sections, normalizedNavbarLinks]);

  const filteredSections = useMemo(() => {
    const isSuperuser = roles.includes('Super User');
    if (isSuperuser) return sections;
    return (sections || []).filter((s) => !s.roles_allowed || s.roles_allowed.some((r) => roles.includes(r)));
  }, [sections, roles]);

  const allTopLevelHeadings = useMemo(() => (filteredSections || []).map((s) => ({
    level: 1, text: s.title, slug: s.slug, id: `section-${s.slug}`,
  })), [filteredSections]);

  const sectionFromUrlRaw = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('section');
  }, [location.search]);

  const [currentSectionParam, setCurrentSectionParam] = useState(null);

  useEffect(() => {
    if (!filteredSections || filteredSections.length === 0) {
      if (!sectionFromUrlRaw) {
        setCurrentSectionParam(null);
      } else {
        setCurrentSectionParam(sectionFromUrlRaw);
      }
      return;
    }

    if (sectionFromUrlRaw) {
      const normalizedSlug = sectionFromUrlRaw.toLowerCase().replace(/_/g, '-').trim();
      const matched = filteredSections.find((s) =>
        s.nav_link_path === normalizedSlug ||
        s.nav_link_path === sectionFromUrlRaw ||
        s.slug === normalizedSlug ||
        s.slug === sectionFromUrlRaw
      );
      if (matched) {
        const param = matched.nav_link_path || matched.slug;
        setCurrentSectionParam(param);
        return;
      }
    }

    if (!currentSectionParam && filteredSections.length > 0) {
      const first = filteredSections[0];
      setCurrentSectionParam(first.nav_link_path || first.slug);
      navigate(`?section=${encodeURIComponent(first.nav_link_path || first.slug)}`, { replace: true });
    }
  }, [sectionFromUrlRaw, filteredSections, navigate, currentSectionParam]);

  const fetchSection = useCallback(async (slug) => {
    if (!slug) return null;
    const res = await getHelpSection(slug);
    return res?.data || null;
  }, []);

  const {
    data: sectionData,
    isLoading: sectionIsLoading,
    refetch: refetchSection,
  } = useQuery(['help-section', currentSectionParam], () => fetchSection(currentSectionParam), {
    enabled: !!currentSectionParam,
    // Prevent auto-refetch after save — we update the cache directly in updateMutation onSuccess
    staleTime: 1000 * 60 * 2,
  });

  useEffect(() => {
    setLoading(Boolean(sectionsIsLoading));
  }, [sectionsIsLoading]);

  useEffect(() => {
    setContentLoading(Boolean(sectionIsLoading));
  }, [sectionIsLoading]);

  useEffect(() => {
    if (sectionData) {
      setActiveSection(sectionData);
      // Normalize incoming content to fix:
      // 1. H1 headings with bold marks (strip them)
      // 2. Empty paragraphs with empty text nodes (causes blank editor)
      const raw = sectionData.content;
      let content = (raw && typeof raw === 'object' && Array.isArray(raw.content))
        ? raw
        : createEmptySection(sectionData.title || '');
      // Apply normalization to fix the content issues
      content = normalizeTipTapContent(content);
      setActiveSectionContent(content);
      // Only reset draft and pending files if we're not currently editing
      // This preserves edits during save operations
      if (!isEditing) {
        setSectionDraft(null);
        setPendingFiles([]);
      }
      if (editorScrollRef.current) editorScrollRef.current.scrollTop = 0;
    } else {
      setActiveSection(null);
      setActiveSectionContent(null);
    }
  }, [sectionData, isEditing]);

  const createMutation = useMutation({
    mutationFn: (payload) => createHelpSection(payload),
    onSuccess: async (res) => {
      await refetchSections();
      const created = res?.data || res;
      if (created) {
        // Use the content from the response, or create a default one with H1
        const hasValidContent = created.content &&
          typeof created.content === 'object' &&
          Array.isArray(created.content.content);
        let content = hasValidContent ? created.content : createEmptySection(created.title || '');
        // Normalize content to fix H1 marks and empty paragraph issues
        content = normalizeTipTapContent(content);

        setActiveSection(created);
        setActiveSectionContent(content);
        setIsEditing(true);
        setSectionDraft(JSON.parse(JSON.stringify(content)));
      }
      const navParam = created?.nav_link_path || created?.slug;
      if (navParam) navigate(`?section=${encodeURIComponent(navParam)}`, { replace: true });
      toast.success('Section created successfully');
    },
    onError: (err) => {
      console.error('create error', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to create section';
      toast.error(msg);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateHelpSection(id, payload),
    onSuccess: async (res, { id, payload }) => {
      // ✅ FIX: Update the query cache directly instead of refetching the section.
      // This prevents the main content area from reloading/flashing after save.
      queryClient.setQueryData(
        ['help-section', currentSectionParam],
        (old) => old ? { ...old, ...payload } : old
      );

      // Only refetch the sidebar sections list (for title updates, ordering, etc.)
      await refetchSections();
      toast.success('Section saved successfully');
    },
    onError: (err) => {
      console.error('update error', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to save section';
      toast.error(msg.includes('upload') || msg.includes('base64') || msg.includes('image') ? msg : 'Failed to save section');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteHelpSection(id),
    onSuccess: async () => {
      const ref = await refetchSections();
      const newSections = ref?.data || [];
      const remaining = newSections.filter((s) => s.id !== sectionToDelete?.id);

      if (remaining.length > 0) {
        const first = remaining[0];
        const navParam = first.nav_link_path || first.slug;
        navigate(`?section=${encodeURIComponent(navParam)}`, { replace: true });
      } else {
        setActiveSection(null);
        setActiveSectionContent(null);
        navigate('/help-documentation', { replace: true });
      }
      setDeleteOpen(false);
      setSectionToDelete(null);
      toast.success('Section deleted successfully');
    },
    onError: (err) => {
      console.error('delete error', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete section';
      toast.error(msg);
    },
  });

  const registerPendingImage = useCallback((file) => {
    const id = `p-${Math.random().toString(36).slice(2, 9)}`;
    setPendingFiles((prev) => [...prev, { id, file }]);
    return id;
  }, []);

  function dataURLtoFile(dataurl, filename = 'pasted-image.png') {
    const arr = dataurl.split(',');
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  }

  const uploadPendingFilesAndReplace = useCallback(async (doc) => {
    setUploadingImages(true);
    const idToResult = {};
    try {
      const base64Srcs = new Set();
      const walkCollectBase64 = (node) => {
        if (Array.isArray(node)) return node.forEach(walkCollectBase64);
        if (node && typeof node === 'object') {
          const attrs = node.attrs || {};
          const src = attrs.src;
          const pid = attrs.pending_id || attrs.pendingId;
          if (typeof src === 'string' && src.startsWith('data:') && !pid) {
            base64Srcs.add(src);
          }
          Object.keys(node).forEach((k) => walkCollectBase64(node[k]));
        }
      };
      walkCollectBase64(doc);

      const extraPending = [];
      const base64ToId = {};
      if (base64Srcs.size > 0) {
        let counter = 0;
        for (const src of base64Srcs) {
          counter += 1;
          const id = `p-${Math.random().toString(36).slice(2, 9)}`;
          const file = dataURLtoFile(src, `pasted-${Date.now()}-${counter}.png`);
          extraPending.push({ id, file, originalSrc: src });
          base64ToId[src] = id;
        }
      }

      const combinedPending = [
        ...(pendingFiles || []).map(({ id, file }) => ({ id, file, originalSrc: null })),
        ...extraPending.map(({ id, file, originalSrc }) => ({ id, file, originalSrc })),
      ];

      if (combinedPending.length === 0) return doc;

      console.info('[upload] combinedPending count:', combinedPending.length);

      const uploads = combinedPending.map(({ id, file }) =>
        uploadHelpImage(file)
          .then((resp) => {
            console.log('[uploadHelpImage] resp for', id, resp);
            const d = resp && resp.data ? resp.data : resp;

            const path = d?.path || d?.filePath || d?.file_path || d?.data?.path || null;
            const url = d?.url || d?.location || d?.fileUrl || d?.file_url || d?.data?.url || null;

            if (!path && !url) {
              if (typeof resp === 'string' && resp.startsWith('http')) {
                idToResult[id] = { path: null, url: resp };
                return;
              }
              idToResult[id] = { raw: resp, error: 'no_path_or_url' };
              return;
            }

            idToResult[id] = { path, url };
          })
          .catch((err) => {
            console.error('Upload failed for', id, err);
            idToResult[id] = null;
          })
      );

      await Promise.all(uploads);

      const failed = combinedPending.filter(({ id }) => {
        const r = idToResult[id];
        return r === null || (typeof r === 'object' && r && r.error);
      });

      if (failed.length > 0) {
        const failedIds = new Set(failed.map(f => f.id));
        const keepFailed = (pendingFiles || [])
          .filter(p => failedIds.has(p.id))
          .concat(extraPending.filter(p => failedIds.has(p.id)).map(p => ({ id: p.id, file: p.file })));
        setPendingFiles(keepFailed);

        const failedNames = failed.map(f => f.file?.name || f.id).join(', ');
        const message = failed.length === 1
          ? `1 image failed to upload: ${failedNames}. Please retry or remove it.`
          : `${failed.length} images failed to upload: ${failedNames}. Please retry or remove them.`;

        toast.error(message);
        return doc;
      }

      const base64Keys = Object.keys(base64ToId);

      const replaceRec = (node) => {
        if (Array.isArray(node)) return node.map(replaceRec);
        if (node && typeof node === 'object') {
          if (node.type === 'image' || (node.attrs && (node.attrs.src || node.attrs['data-path']))) {
            const attrs = { ...(node.attrs || {}) };
            const pid = attrs.pending_id || attrs.pendingId;
            const src = attrs.src;

            if (pid && idToResult[pid] && !idToResult[pid].error) {
              const { path, url } = idToResult[pid];

              attrs.src = (url || path);
              delete attrs.pending_id;
              delete attrs.pendingId;

              if (url) attrs['data-url'] = url;
              return { ...node, attrs };
            }

            if (typeof src === 'string' && src.startsWith('data:')) {
              let mappedId = base64ToId[src];

              if (!mappedId && base64Keys.length) {
                const k = base64Keys.find(k => src.startsWith(k));
                if (k) mappedId = base64ToId[k];
              }

              if (!mappedId) {
                const cand = extraPending.find(ep => ep.originalSrc && src.includes(ep.originalSrc.slice(0, 100)));
                if (cand) mappedId = cand.id;
              }

              if (mappedId && idToResult[mappedId] && !idToResult[mappedId].error) {
                const { path, url } = idToResult[mappedId];
                attrs.src = (url || path);
                if (url) attrs['data-url'] = url;
                return { ...node, attrs };
              }

              console.warn('[upload] could not map pasted base64 image to uploaded id for node', node);
              return { ...node, attrs };
            }

            if (attrs['data-path']) {
              attrs.src = attrs['data-path'];
              return { ...node, attrs };
            }
          }

          const out = {};
          for (const k of Object.keys(node)) out[k] = replaceRec(node[k]);
          return out;
        }
        return node;
      };

      const newDoc = replaceRec(doc);

      setPendingFiles([]);
      return newDoc;
    } finally {
      setUploadingImages(false);
    }
  }, [pendingFiles]);

  const saveEdit = useCallback(async () => {
    if (!activeSection || !sectionDraft) {
      toast.error('No section to save');
      return;
    }

    // Validate content structure
    if (!sectionDraft?.content || !Array.isArray(sectionDraft.content)) {
      toast.error('Invalid content structure');
      return;
    }

    setSaving(true);
    try {
      // Extract title from first H1 heading, or use existing title
      let title = activeSection.title || 'Untitled Section';
      const contentArray = sectionDraft.content;
      const firstHeading = contentArray.find((n) => n?.type === 'heading' && n?.attrs?.level === 1);
      if (firstHeading?.content?.[0]?.text?.trim()) {
        title = firstHeading.content[0].text.trim();
      }

      // Upload any pending images
      let docWithUploadedImages = await uploadPendingFilesAndReplace(sectionDraft);

      // Normalize content before saving (fixes H1 marks and empty paragraphs)
      docWithUploadedImages = normalizeTipTapContent(docWithUploadedImages);

      // Save to backend
      await updateMutation.mutateAsync({
        id: activeSection.id,
        payload: { title, content: docWithUploadedImages }
      });

      // ✅ FIX: Update local state directly here so the main content area
      // doesn't reload or flash — the cache update in onSuccess handles React Query,
      // and these state updates handle the local component display.
      setActiveSectionContent(docWithUploadedImages);
      setActiveSection((prev) => ({ ...prev, title }));
      setIsEditing(false);
      setSectionDraft(null);

    } catch (err) {
      console.error('saveEdit error:', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to save section';
      toast.error(msg.includes('upload') || msg.includes('base64') || msg.includes('image') ? msg : 'Failed to save section');
    } finally {
      setSaving(false);
    }
  }, [activeSection, sectionDraft, uploadPendingFilesAndReplace, updateMutation]);

  const startEdit = useCallback(() => {
    if (!activeSection || !activeSectionContent) {
      toast.error('No section selected to edit');
      return;
    }

    // Create a deep copy of the content for editing
    const content = activeSectionContent || { type: 'doc', content: [] };
    setIsEditing(true);
    setSectionDraft(JSON.parse(JSON.stringify(content)));
  }, [activeSection, activeSectionContent]);

  const cancelEdit = useCallback(() => {
    // Clear pending files and draft
    setPendingFiles([]);
    setSectionDraft(null);
    setIsEditing(false);
  }, []);

  const handleNavLinkChange = (val) => {
    const trimmed = val?.trim() || '';
    setSelectedNavLink(trimmed);

    if (!trimmed) {
      if (!titleEdited) setNewTitle('');
      return;
    }
    const linkObj = availableNavLinks.find((ln) => ln.path === trimmed);
    if (linkObj && !titleEdited) {
      setNewTitle(linkObj.label || '');
    }
  };

  const handleTitleChange = (val) => {
    setNewTitle(val);
    setTitleEdited(true);
  };

  const confirmAddSection = useCallback(async () => {
    if (!newTitle.trim()) {
      toast.error('Please enter a section title');
      return;
    }
    setSaving(true);
    try {
      // When attached to a nav link, use that as nav_link_path
      // Otherwise, generate slug from title
      const navLink = selectedNavLink || null;
      const emptyContent = createEmptySection(newTitle);
      const payload = {
        title: newTitle,
        nav_link_path: navLink,
        content: emptyContent,
      };

      await createMutation.mutateAsync(payload);

      // Reset form state
      setNewTitle('');
      setSelectedNavLink('');
      setTitleEdited(false);
      setAddOpen(false);
    } catch (err) {
      console.error('confirmAddSection:', err);
      toast.error('Failed to create section');
    } finally {
      setSaving(false);
    }
  }, [newTitle, selectedNavLink, createMutation]);

  const openDeleteConfirmation = useCallback(() => {
    if (!activeSection) {
      toast.error('No section selected to delete');
      return;
    }
    setSectionToDelete(activeSection);
    setDeleteOpen(true);
  }, [activeSection]);

  const confirmDeleteSection = useCallback(async () => {
    if (!sectionToDelete) return;
    setDeleting(true);
    try {
      await deleteMutation.mutateAsync(sectionToDelete.id);
    } catch (err) {
      console.error('confirmDeleteSection:', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete section';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  }, [sectionToDelete, deleteMutation]);

  const getCurrentIndex = useCallback(() => {
    if (!activeSection) return -1;
    const key = activeSection.nav_link_path || activeSection.slug;
    return filteredSections.findIndex(s => (s.nav_link_path || s.slug) === key);
  }, [activeSection, filteredSections]);

  const goToIndex = useCallback(async (idx) => {
    if (idx < 0 || idx >= filteredSections.length) return;
    const section = filteredSections[idx];
    const navParam = section.nav_link_path || section.slug;
    navigate(`?section=${encodeURIComponent(navParam)}`);
  }, [filteredSections, navigate]);

  const goPrev = useCallback(() => {
    if (isEditing) {
      toast.error('Please save or cancel your edit before navigating.');
      return;
    }
    const idx = getCurrentIndex();
    if (idx > 0) goToIndex(idx - 1);
  }, [getCurrentIndex, goToIndex, isEditing]);

  const goNext = useCallback(() => {
    if (isEditing) {
      toast.error('Please save or cancel your edit before navigating.');
      return;
    }
    const idx = getCurrentIndex();
    if (idx >= 0 && idx < filteredSections.length - 1) goToIndex(idx + 1);
  }, [getCurrentIndex, goToIndex, filteredSections.length, isEditing]);

  const onEditorChange = useCallback((doc) => {
    // only accept well-formed doc objects (prevent accidental null/primitive writes)
    if (isEditing && doc && typeof doc === 'object' && Array.isArray(doc.content)) {
      setSectionDraft(doc);
    }
  }, [isEditing]);

  useEffect(() => {
    if (!addOpen) {
      setNewTitle('');
      setSelectedNavLink('');
      setTitleEdited(false);
    }
  }, [addOpen]);

  const handleSidebarNavigate = useCallback((slug) => {

    if (isEditing) {
      toast.error('Please save or cancel your edit before navigating to another section.');
      return;
    }

    if (!slug) return;
    const parts = slug.split('#');
    const parent = parts[0];
    const target = parts[1] || null;

    const section = filteredSections.find((x) => (x.slug === parent || x.nav_link_path === parent));
    if (section) {
      const navParam = section.nav_link_path || section.slug;
      navigate(`?section=${encodeURIComponent(navParam)}`);

      if (target) {
        setScrollTarget(target);
      } else {
        setScrollTarget(null);
      }
    }
  }, [isEditing, filteredSections, navigate]);

  useEffect(() => {

    if (!scrollTarget) return;

    const escapeSelector = (s) => s.replace(/([ #;.?,+*~'"!:\\[\]()<>$^=|\/\\])/g, '\\$1');

    const tryScrollTo = () => {
      if (!editorScrollRef.current) return false;
      const root = editorScrollRef.current;
      const candidates = [
        `#${escapeSelector(scrollTarget)}`,
        `#heading-${escapeSelector(scrollTarget)}`,
        `[data-heading-slug="${escapeSelector(scrollTarget)}"]`,
        `[id*="${escapeSelector(scrollTarget)}"]`,
      ];

      for (const sel of candidates) {
        try {
          const el = root.querySelector(sel);
          if (el) {
            const top = el.getBoundingClientRect().top - root.getBoundingClientRect().top + root.scrollTop - 12;
            root.scrollTo({ top, behavior: 'smooth' });
            return true;
          }
        } catch (e) {

        }
      }
      return false;
    };

    let attempts = 0;
    const maxAttempts = 12;
    const interval = 120;

    const runner = () => {
      const ok = tryScrollTo();
      if (!ok && attempts < maxAttempts) {
        attempts += 1;
        setTimeout(runner, interval);
        return;
      }

      setScrollTarget(null);
    };

    setTimeout(runner, 100);
  }, [scrollTarget, activeSectionContent]);

  const combinedHeadings = useMemo(() => {
    const result = [];

    filteredSections.forEach(section => {
      if (!section) return;

      result.push({
        level: 1,
        text: section.title || 'Untitled Section',
        slug: section.slug || '',
      });

      const content = section.content?.content;
      const internal = Array.isArray(content) ? extractHeadings(content) : [];

      internal.forEach(h => {
        result.push({
          level: Math.min(h.level + 1, 3),
          text: h.text || '',
          slug: `${section.slug || ''}#${h.slug}`,
        });
      });
    });

    return result;
  }, [filteredSections]);

  const startEditDisabled = !activeSection;

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <button
            type="button"
            className={styles.backIconBtn}
            onClick={() => navigate('/')}
            aria-label="Back"
            title="Back"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
        </div>

        <h1 className={styles.title}>
          Help Documentation
          {activeSection && <span className={styles.titleSuffix}> — {activeSection.title}</span>}
        </h1>
        {isTocauser && isSuperAdmin && (
        <div className={styles.headerRight}>
          <Button onClick={() => setAddOpen(true)} disabled={isEditing || saving}>
            + Add Section
          </Button>

          {!isEditing ? (
            <>
              <Button onClick={startEdit} disabled={startEditDisabled}>
                <FontAwesomeIcon icon={faEdit} /> Edit
              </Button>
              <Button
                onClick={openDeleteConfirmation}
                disabled={!activeSection || isEditing}
                variant="ghost"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </>
          ) : (
            <>
              <Button onClick={saveEdit} variant="primary" disabled={saving || uploadingImages}>
                {saving || uploadingImages ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Save'}
              </Button>
              <Button onClick={cancelEdit} variant="ghost" disabled={saving || uploadingImages}>
                Cancel
              </Button>
            </>
          )}
        </div>)}
      </header>

      {isEditing && (
        // render toolbar while editing; toolbar should handle null editor reference initially
        <EditorToolbar
          editor={editorRef.current}
          isEditable
          onRegisterPendingImage={registerPendingImage}
        />
      )}

      <div className={styles.contentArea}>
        <aside className={styles.sidebar}>
          {loading ? (
            <div className={styles.sidebarLoader}>
              <Spinner />
            </div>
          ) : (
            <ProfessionalSidebar
              headings={combinedHeadings}
              activeHeadingSlug={activeSection?.slug}
              onNavigate={handleSidebarNavigate}
            />
          )}
        </aside>

       <main className={styles.editorContainer} ref={editorScrollRef}>
  {loading ? (
    <div className={styles.contentLoading}>
      <Spinner />
    </div>
  ) : contentLoading ? (
    <div className={styles.contentLoading}>
      <Spinner />
    </div>
  ) : activeSection ? (
    <>
      <TiptapEditor
        content={isEditing
          ? (sectionDraft || createEmptySection(activeSection?.title || ''))
          : (activeSectionContent || createEmptySection(activeSection?.title || ''))}
        onChange={onEditorChange}
        isEditable={isEditing}
        editorRef={editorRef}
        headings={extractHeadings((activeSectionContent && activeSectionContent.content) || [])}
        onImageClick={handleImageClick}
      />

      <div className={styles.paginationInline}>
        <Button onClick={goPrev} disabled={isEditing || getCurrentIndex() <= 0 || contentLoading}>Prev</Button>
        <div className={styles.pageIndicator}>
          {filteredSections.length > 0 && getCurrentIndex() >= 0
            ? `${getCurrentIndex() + 1} / ${filteredSections.length}`
            : ''}
        </div>
        <Button onClick={goNext} disabled={isEditing || getCurrentIndex() >= filteredSections.length - 1 || contentLoading}>Next</Button>
      </div>
    </>
  ) : (
    <div className={styles.emptyState}>
      <p>No section selected. Choose a section from the sidebar or create a new one.</p>
    </div>
  )}
</main>
      </div>

      <Dialog open={addOpen} setOpen={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Section</DialogTitle></DialogHeader>
          <DialogBody>
            <input
              className={styles.input}
              placeholder="Section title"
              value={newTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              autoFocus
            />
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>Attach to nav link (optional)</label>

              <div className={styles.selectWrapper}>
                <Select
                  value={selectedNavLink}
                  onChange={(val) => handleNavLinkChange(val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a nav link" />
                  </SelectTrigger>

                  <SelectContent searchField>
                    <SelectItem value="">None</SelectItem>

                    {availableNavLinks.map((ln) => (
                      <SelectItem key={ln.path} value={ln.path}>
                        {ln.label} ({ln.path})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {availableNavLinks.length === 0 && (
                <div className={styles.helperText}>
                  No available navbar links left to attach.
                </div>
              )}
            </div>

          </DialogBody>
          <DialogFooter>
            <Button onClick={() => { setAddOpen(false); setSelectedNavLink(''); setNewTitle(''); setTitleEdited(false); }} variant="ghost">Cancel</Button>
            <Button onClick={confirmAddSection} variant="primary" disabled={saving || !newTitle.trim()}>
              {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} setOpen={setDeleteOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Section</DialogTitle></DialogHeader>
          <DialogBody>
            <p>Are you sure you want to delete <strong>"{sectionToDelete?.title}"</strong>? This action cannot be undone.</p>
          </DialogBody>
          <DialogFooter>
            <Button onClick={() => { setDeleteOpen(false); setSectionToDelete(null); }} variant="ghost">Cancel</Button>
            <Button onClick={confirmDeleteSection} variant="primary" disabled={deleting} style={{ backgroundColor: '#dc2626' }}>
              {deleting ? <FontAwesomeIcon icon={faSpinner} spin /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={imageDialogOpen} setOpen={setImageDialogOpen}>
        <DialogContent style={{ maxWidth: '900px', width: '100%' }}>
          <DialogHeader>
            <DialogTitle>Image</DialogTitle>
          </DialogHeader>

          <DialogBody>
            <div style={{ textAlign: 'center', minHeight: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {imageDialogSrc ? (
                // keep image responsive and constrained
                <img
                  src={imageDialogSrc}
                  alt=""
                  style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 6, objectFit: 'contain' }}
                  onError={(e) => {
                    console.error('Image failed to load:', imageDialogSrc);
                    e.target.style.display = 'none';
                  }}
                />
              ) : null}
            </div>
          </DialogBody>

          <DialogFooter>
            <Button onClick={() => setImageDialogOpen(false)} variant="primary">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
}